/**
 * App layout with persistent sidebar for authenticated users.
 *
 * Web owns the page layout (sidebar + main content grid).
 * Breadcrumbs are auto-generated from the URL path.
 */

import * as stylex from "@stylexjs/stylex";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useLayoutEffect, useState } from "react";

import {
  AppSidebar,
  type Breadcrumb,
  NavigationProvider,
  PageHeader,
  SIDEBAR_COLLAPSED_WIDTH,
  SidebarToggleIcon,
  useBreadcrumbStore,
  useResponsiveSidebar,
  useSidebarResize,
  useSidebarStore,
} from "@animus/dom-features";
import {
  useAuthStore,
  useSidebarUser,
} from "@animus/frontend-features/auth-store";
import { animations, ResizeHandle, toast } from "@animus/primaris";

import { WebNavLink } from "../../navigation/nav-link.tsx";
import { webLayout } from "../../styles.stylex.ts";

const PATH_LABELS: Record<string, string> = {
  admin: "Admin",
  users: "Users",
  foods: "Foods",
  modifiers: "Modifiers",
  releases: "Releases",
  debug: "Debug",
};

function titleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const styles = stylex.create({
  toggleButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    padding: 0,
    borderWidth: 0,
    borderRadius: 8,
    backgroundColor: "transparent",
    cursor: "pointer",
    transition: "background-color 150ms ease-out",
  },
  toggleButtonHover: {
    backgroundColor: "var(--primaris-background-subtle)",
  },
});

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const sidebarUser = useSidebarUser();
  const [isToggleHovered, setIsToggleHovered] = useState(false);
  const [isDenied, setIsDenied] = useState(false);

  const {
    isCollapsed,
    width,
    expandedSections,
    toggleCollapsed,
    toggleSection,
    setUserPreference,
  } = useSidebarStore();
  const { isWindowNarrow, canExpand } = useResponsiveSidebar();
  const {
    isResizing,
    expandProgress,
    elasticWidth,
    cursorX,
    cursorY,
    handleMouseDown,
  } = useSidebarResize();

  const setBreadcrumbs = useBreadcrumbStore((s) => s.setBreadcrumbs);

  useLayoutEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs: Breadcrumb[] = [{ label: "Home", path: "/" }];

    let currentPath = "";
    segments.forEach((segment, i) => {
      currentPath += `/${segment}`;
      const isLast = i === segments.length - 1;

      breadcrumbs.push({
        label: PATH_LABELS[segment] ?? titleCase(segment),
        ...(isLast ? {} : { path: currentPath }),
      });
    });

    setBreadcrumbs(segments.length > 0 ? breadcrumbs : []);
  }, [location.pathname, setBreadcrumbs]);

  async function handleSignOut() {
    await signOut();
    navigate({ to: "/login" });
  }

  function handleSignIn() {
    navigate({ to: "/login" });
  }

  function handleBreadcrumbNavigate(path: string) {
    navigate({ to: path });
  }

  function handleToggleClick() {
    if (isCollapsed && isWindowNarrow) {
      setIsDenied(true);
      setUserPreference(false);
      toast("Sidebar needs more space to stretch out");
      return;
    }
    toggleCollapsed();
  }

  const sidebarWidth =
    elasticWidth ?? (isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : width);

  return (
    <NavigationProvider slots={{ NavLink: WebNavLink }}>
      <div
        data-resizing={isResizing}
        data-sidebar-wrapper
        style={
          {
            "--sidebar-width": `${sidebarWidth}px`,
            "--sidebar-expand-progress": expandProgress,
          } as React.CSSProperties
        }
        {...stylex.props(
          webLayout.container,
          isResizing && webLayout.containerResizing
        )}
      >
        <AppSidebar
          expandedSections={expandedSections}
          isCollapsed={isCollapsed}
          onCollapsedChange={toggleCollapsed}
          onSectionToggle={toggleSection}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          user={sidebarUser}
        />
        <main {...stylex.props(webLayout.main)}>
          <ResizeHandle
            canExpand={canExpand}
            cursorX={cursorX}
            cursorY={cursorY}
            expandProgress={expandProgress}
            isResizing={isResizing}
            onMouseDown={handleMouseDown}
          />
          <PageHeader
            onNavigate={handleBreadcrumbNavigate}
            toggleSlot={
              <button
                {...stylex.props(
                  styles.toggleButton,
                  isToggleHovered && styles.toggleButtonHover,
                  isDenied && animations.shake,
                  isDenied && animations.pulseRed,
                  animations.reducedMotion
                )}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                onAnimationEnd={() => setIsDenied(false)}
                onClick={handleToggleClick}
                onMouseEnter={() => setIsToggleHovered(true)}
                onMouseLeave={() => setIsToggleHovered(false)}
                type="button"
              >
                <SidebarToggleIcon
                  isCollapsed={isCollapsed}
                  isDenied={isDenied}
                  isHovered={isToggleHovered}
                />
              </button>
            }
          />
          <Outlet />
        </main>
      </div>
    </NavigationProvider>
  );
}
